// What was in force on a day, who approved it and who had read it by then (rule 37): the auditor's question, in one
// read, from append-only rows.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/DataTable';
import { fieldClass, labelClass, officeFocus, useResource } from '../../shells/OfficeShell';
import { stamp } from './goodsInModel';
import { STATUS_LABEL, brisbaneToday, type AuditPayload } from './documentsModel';

type Row = AuditPayload['documents'][number];
const col = createColumnHelper<Row>();

export function DocumentsAudit() {
  const [day, setDay] = useState(brisbaneToday());
  const res = useResource<AuditPayload>(day ? `/documents/audit?asOf=${day}` : null);
  const columns = [
    col.accessor((r) => r.docId, {
      id: 'doc',
      header: 'Document',
      cell: (c) => (
        <Link to={`/office/documents/${c.row.original.id}`} className={`font-mono underline ${officeFocus}`}>
          {c.getValue()}
        </Link>
      ),
      meta: { rowHeader: true, nowrap: true },
    }),
    col.accessor((r) => r.title, { id: 'title', header: 'Title' }),
    col.accessor((r) => r.versionNo, { id: 'version', header: 'Version', cell: (c) => `v${c.getValue()}`, meta: { nowrap: true } }),
    col.accessor((r) => r.effectiveFrom, { id: 'from', header: 'In force from', meta: { nowrap: true } }),
    col.accessor((r) => r.status, { id: 'status', header: 'Status now', cell: (c) => STATUS_LABEL[c.getValue()] }),
    col.accessor((r) => r.approvedByUpn ?? '', {
      id: 'approved',
      header: 'Approved',
      cell: (c) => (
        <>
          {c.getValue() || 'not known'}
          <div className="text-(length:--fs-sm) text-(--muted)">{stamp(c.row.original.approvedAt)}</div>
        </>
      ),
    }),
    col.accessor((r) => r.acknowledgedBy.length, {
      id: 'read',
      header: 'Read by then',
      cell: (c) => <span title={c.row.original.acknowledgedBy.map((a) => a.upn).join(', ')}>{c.getValue()}</span>,
      meta: { align: 'right', nowrap: true },
    }),
  ];
  return (
    <div className="flex flex-col gap-(--s4)">
      <header>
        <Link to="/office/documents" className={`text-(length:--fs-sm) underline ${officeFocus}`}>
          Documents
        </Link>
        <h1 className="m-0 mt-(--s1) font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">In force on a date</h1>
      </header>
      <label className="flex w-fit flex-col gap-(--s1)">
        <span className={labelClass}>The day</span>
        <input type="date" className={`${fieldClass} ${officeFocus}`} max={brisbaneToday()} value={day} onChange={(e) => setDay(e.target.value)} />
      </label>
      {res.error ? (
        <div role="alert" className="flex flex-wrap items-center gap-(--s2)">
          <p className="m-0 font-semibold text-(--warn)">Could not read it: {res.error} This is not the same as "none".</p>
          <Button variant="outline" type="button" onClick={res.reload}>
            Try again
          </Button>
        </div>
      ) : (
        <DataTable data={res.data?.documents ?? []} columns={columns} caption={`Documents in force on ${day}`} getRowId={(r) => `${r.id}-${r.versionNo}`} loading={res.loading} stickyFirst empty={res.data ? 'No document was in force that day.' : null} />
      )}
    </div>
  );
}

export default DocumentsAudit;
